'use client';

import { AnswerTemplateVariableHint } from '@/components/answer-templates/AnswerTemplateVariableHint';
import clsx from 'clsx';
import { FC } from 'react';
import type { AnswerTemplatesBindings } from '@/components/answer-templates/use-answer-templates';
import { UIPriority } from '@/enums/ui-priorities';
import { ReviewAnswerTemplateEntity } from '@/entities/review-answer-template.entity';
import Button from '@/ui/Button';
import Input from '@/ui/Input';
import Modal from '@/ui/Modal';
import Textarea from '@/ui/Textarea';

export type AnswerTemplatesEditorContentProps = {
    templates: ReviewAnswerTemplateEntity[];
    isLoading?: boolean;
    templatesErrorText?: string | null;
    editingTemplateId: string | null;
    templateNameDraft: string;
    templateTextDraft: string;
    templateFormErrorText: string | null;
    isSaving: boolean;
    isDeleting: boolean;
    canSave: boolean;
    canDelete: boolean;
    onSelectTemplate: (templateId: string) => void;
    onStartNewTemplate: () => void;
    onTemplateNameChange: (value: string) => void;
    onTemplateTextChange: (value: string) => void;
    onSave: () => Promise<void>;
    onDelete: () => Promise<void>;
    showTitle?: boolean;
    className?: string;
};

type ModalAnswerTemplatesEditorProps = AnswerTemplatesEditorContentProps & {
    isShown: boolean;
    setIsShown: (value: boolean) => void;
};

type AnswerTemplatesEditorContentFromBindingsProps = {
    bindings: AnswerTemplatesBindings;
    showTitle?: boolean;
    className?: string;
};

type ModalAnswerTemplatesEditorFromBindingsProps = {
    bindings: AnswerTemplatesBindings;
    isShown?: boolean;
    setIsShown?: (value: boolean) => void;
};

const mapAnswerTemplatesBindingsToEditorProps = (
    bindings: AnswerTemplatesBindings,
): Omit<AnswerTemplatesEditorContentProps, 'showTitle' | 'className'> => ({
    templates: bindings.answerTemplates,
    isLoading: bindings.isTemplatesLoading,
    templatesErrorText: bindings.templatesErrorText,
    editingTemplateId: bindings.editingTemplateId,
    templateNameDraft: bindings.templateNameDraft,
    templateTextDraft: bindings.templateTextDraft,
    templateFormErrorText: bindings.templateFormErrorText,
    isSaving: bindings.isTemplateSaving,
    isDeleting: bindings.isTemplateDeleting,
    canSave: bindings.canSaveTemplate,
    canDelete: bindings.canDeleteTemplate,
    onSelectTemplate: bindings.onSelectTemplateForEdit,
    onStartNewTemplate: bindings.onStartNewTemplateDraft,
    onTemplateNameChange: bindings.onTemplateNameDraftChange,
    onTemplateTextChange: bindings.onTemplateTextDraftChange,
    onSave: bindings.onSaveTemplate,
    onDelete: bindings.onDeleteTemplate,
});

const ANSWER_TEMPLATES_EDITOR_ROOT_CLASS_NAME = 'w-full h-full min-h-0 overflow-hidden flex flex-col bg-white';
const ANSWER_TEMPLATES_EDITOR_SCROLL_CLASS_NAME = 'min-h-0 flex-1 overflow-y-auto p-4 flex flex-col gap-3';
const ANSWER_TEMPLATES_EDITOR_ACTIONS_CLASS_NAME = 'shrink-0 border-t border-slate-200 bg-white p-4 pt-3';

export const AnswerTemplatesEditorContent: FC<AnswerTemplatesEditorContentProps> = ({
    templates,
    isLoading = false,
    templatesErrorText,
    editingTemplateId,
    templateNameDraft,
    templateTextDraft,
    templateFormErrorText,
    isSaving,
    isDeleting,
    canSave,
    canDelete,
    onSelectTemplate,
    onStartNewTemplate,
    onTemplateNameChange,
    onTemplateTextChange,
    onSave,
    onDelete,
    showTitle = false,
    className,
}) => {
    return (
        <div className={clsx(ANSWER_TEMPLATES_EDITOR_ROOT_CLASS_NAME, className)} data-answer-templates-editor="root">
            <div className={ANSWER_TEMPLATES_EDITOR_SCROLL_CLASS_NAME} data-answer-templates-editor="scroll-body">
                {showTitle ? (
                    <div className="shrink-0">
                        <h2 className="text-base font-semibold text-slate-900">Шаблоны ответов на отзывы</h2>
                    </div>
                ) : null}
                <div className="flex max-h-[min(30dvh,180px)] shrink-0 flex-col gap-1 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-2">
                    {isLoading ? (
                        <p className="px-2 py-1.5 text-sm text-slate-500">Загрузка шаблонов...</p>
                    ) : templatesErrorText ? (
                        <p className="px-2 py-1.5 text-sm text-red-600">{templatesErrorText}</p>
                    ) : (
                        templates.map((template) => (
                            <button
                                key={template.id}
                                type="button"
                                className={`w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${
                                    template.id === editingTemplateId
                                        ? 'bg-slate-900 text-white'
                                        : 'bg-white text-slate-700 hover:bg-slate-100'
                                }`}
                                onClick={() => onSelectTemplate(template.id)}
                            >
                                {template.name}
                            </button>
                        ))
                    )}
                    <button
                        type="button"
                        className="w-full rounded px-2 py-1.5 text-left text-sm text-slate-600 hover:bg-white transition-colors"
                        onClick={onStartNewTemplate}
                    >
                        + Новый шаблон
                    </button>
                </div>

                <div className="flex shrink-0 flex-col gap-2">
                    <label className="text-xs text-slate-600" htmlFor="answer-template-name-input">
                        Название шаблона
                    </label>
                    <Input
                        id="answer-template-name-input"
                        aria-label="Название шаблона"
                        value={templateNameDraft}
                        onChange={(event) => onTemplateNameChange(event.target.value)}
                    />
                </div>

                <div className="flex min-h-[140px] flex-1 flex-col gap-2">
                    <label className="text-xs text-slate-600" htmlFor="answer-template-text-input">
                        Ответ
                    </label>
                    <Textarea
                        id="answer-template-text-input"
                        ariaLabel="Ответ"
                        value={templateTextDraft}
                        setValue={onTemplateTextChange}
                        className="min-h-[120px] flex-1 border border-slate-200"
                    />
                    <AnswerTemplateVariableHint />
                </div>

                {templateFormErrorText && <p className="shrink-0 text-red-600 text-xs">{templateFormErrorText}</p>}
            </div>

            <div className={ANSWER_TEMPLATES_EDITOR_ACTIONS_CLASS_NAME} data-answer-templates-editor="actions">
                <div className="grid grid-cols-2 gap-2">
                    <Button
                        type="button"
                        priorityType={UIPriority.PRIMARY}
                        uiSize="compact"
                        disabled={!canSave || isDeleting}
                        onClick={() => void onSave()}
                    >
                        {isSaving ? 'Сохраняем...' : 'Сохранить'}
                    </Button>
                    <Button
                        type="button"
                        priorityType={UIPriority.SECONDARY}
                        uiSize="compact"
                        disabled={!canDelete}
                        onClick={() => void onDelete()}
                    >
                        {isDeleting ? 'Удаляем...' : 'Удалить'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const AnswerTemplatesEditorContentFromBindings: FC<AnswerTemplatesEditorContentFromBindingsProps> = ({
    bindings,
    showTitle,
    className,
}) => (
    <AnswerTemplatesEditorContent
        {...mapAnswerTemplatesBindingsToEditorProps(bindings)}
        showTitle={showTitle}
        className={className}
    />
);

export const ModalAnswerTemplatesEditor: FC<ModalAnswerTemplatesEditorProps> = ({
    isShown,
    setIsShown,
    ...contentProps
}) => {
    return (
        <Modal
            isShown={isShown}
            setIsShown={setIsShown}
            title="Шаблоны ответов на отзывы"
            closable
            className="!h-[82dvh] max-h-[calc(100dvh-12px)] min-h-0 !w-[calc(100vw-12px)] max-w-[640px] overflow-hidden"
        >
            <AnswerTemplatesEditorContent {...contentProps} />
        </Modal>
    );
};

export const ModalAnswerTemplatesEditorFromBindings: FC<ModalAnswerTemplatesEditorFromBindingsProps> = ({
    bindings,
    isShown = bindings.isTemplatesEditorShown,
    setIsShown,
}) => {
    const handleShownChange =
        setIsShown ??
        ((value: boolean) => {
            if (!value) {
                bindings.onCloseTemplatesEditor();
            }
        });

    return (
        <ModalAnswerTemplatesEditor
            isShown={isShown}
            setIsShown={handleShownChange}
            {...mapAnswerTemplatesBindingsToEditorProps(bindings)}
        />
    );
};
